import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ArrowLeft, MapPin, Compass, Users, Building, Trees, Mountain, Waves, Info, Lock, ChevronRight } from 'lucide-react';
import { getRealmById, getRealmLocations, LOCATION_TYPES } from '../config/worldData';

// Get icon for location type
const getLocationIcon = (type) => {
  switch (type) {
    case LOCATION_TYPES.TOWN:
    case LOCATION_TYPES.VILLAGE:
      return Building;
    case LOCATION_TYPES.CITY:
      return Building;
    case LOCATION_TYPES.WILDERNESS:
      return Trees;
    case LOCATION_TYPES.MOUNTAIN:
      return Mountain;
    case LOCATION_TYPES.RIVER:
      return Waves;
    case LOCATION_TYPES.TAVERN:
      return Users;
    default:
      return MapPin;
  }
};

export default function RegionMapPage() {
  const { realmId } = useParams();
  const navigate = useNavigate();
  const [realm, setRealm] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  useEffect(() => {
    const foundRealm = getRealmById(realmId);
    if (foundRealm) {
      setRealm(foundRealm);
    } else {
      // Realm not found, go back to world map
      navigate('/world');
    }
  }, [realmId, navigate]);

  const handleBack = () => {
    navigate('/world');
  };

  // Get locations from centralized config
  const locations = getRealmLocations(realmId);

  if (!realm) {
    return (
      <div className="mg-stage bg-black flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-amber-900/30 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div 
      className="mg-stage"
      style={{ backgroundColor: '#0a0a0a' }}
      data-testid="region-map-page"
    >
      {/* Themed Background Gradient */}
      <div className={`absolute inset-0 bg-gradient-to-br ${realm.theme.bgGradient} opacity-50`} />
      
      {/* Noise Texture */}
      <div 
        className="absolute inset-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%' height='100%' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-20 p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-black/50 backdrop-blur-sm border border-amber-900/30 hover:border-amber-500/50 transition-all"
            data-testid="back-to-world"
          >
            <ArrowLeft className="w-5 h-5 text-amber-100" />
            <span className="text-amber-100 text-sm hidden sm:inline">World Map</span>
          </button>
        </div>

        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-black/50 backdrop-blur-sm border border-amber-900/30">
          <Compass className="w-5 h-5" style={{ color: realm.theme.primary }} />
          <span className="font-heading text-amber-100 text-sm uppercase tracking-wider">{realm.name}</span>
        </div>

        <button
          onClick={() => setShowInfo(!showInfo)}
          className="p-2 rounded-full bg-black/50 backdrop-blur-sm border border-amber-900/30 hover:border-amber-500/50 transition-all"
          data-testid="region-info-btn"
        >
          <Info className="w-5 h-5 text-amber-100" />
        </button>
      </header>

      {/* Info Panel */}
      {showInfo && (
        <div className="absolute top-20 right-4 z-30 w-72 p-4 rounded-xl bg-black/80 backdrop-blur-md border border-amber-900/30 animate-fadeIn">
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="w-4 h-4" style={{ color: realm.theme.primary }} />
            <span className="text-xs uppercase tracking-wider" style={{ color: realm.theme.primary }}>
              {realm.subtitle}
            </span>
          </div>
          <h3 className="font-heading text-amber-100 text-lg mb-2">{realm.name}</h3>
          <p className="text-sm text-amber-100/70 leading-relaxed">
            {realm.description}
          </p>
          <div className="mt-3 pt-3 border-t border-amber-900/30">
            <p className="text-xs text-amber-500/70">
              {locations.length} locations to explore
            </p>
          </div>
        </div>
      )}

      {/* Main Content - Placeholder Map */}
      <div className="absolute inset-0 flex items-center justify-center p-4 pt-20 pb-8">
        <div className="w-full max-w-4xl h-full flex flex-col">
          {/* Placeholder Map Visual */}
          <div className="flex-1 relative rounded-2xl overflow-hidden border-2 border-amber-900/30" style={{ minHeight: '300px' }}>
            {/* Placeholder background pattern */}
            <div 
              className="absolute inset-0"
              style={{
                background: `
                  radial-gradient(circle at 30% 40%, ${realm.theme.primary}15 0%, transparent 50%),
                  radial-gradient(circle at 70% 60%, ${realm.theme.secondary}10 0%, transparent 40%),
                  linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.7) 100%)
                `,
              }}
            />

            {/* Compass Rose */}
            <div className="absolute top-4 right-4 w-16 h-16 opacity-30">
              <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="50" cy="50" r="48" stroke={realm.theme.primary} strokeWidth="1" opacity="0.5" />
                <path d="M50 5 L55 45 L50 50 L45 45 Z" fill={realm.theme.primary} />
                <path d="M95 50 L55 55 L50 50 L55 45 Z" fill={realm.theme.primary} opacity="0.7" />
                <path d="M50 95 L45 55 L50 50 L55 55 Z" fill={realm.theme.primary} opacity="0.5" />
                <path d="M5 50 L45 45 L50 50 L45 55 Z" fill={realm.theme.primary} opacity="0.3" />
                <text x="50" y="15" textAnchor="middle" fill={realm.theme.primary} fontSize="8" fontFamily="serif">N</text>
              </svg>
            </div>

            {/* "Coming Soon" Overlay */}
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
              <div 
                className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
                style={{ 
                  backgroundColor: `${realm.theme.primary}20`,
                  border: `2px solid ${realm.theme.primary}40`
                }}
              >
                <MapPin className="w-10 h-10" style={{ color: realm.theme.primary }} />
              </div>
              <h2 className="font-heading text-2xl md:text-3xl text-white mb-2">
                {realm.name} Awaits
              </h2>
              <p className="text-amber-100/60 max-w-md text-sm md:text-base">
                The detailed map of {realm.name} is being charted by our cartographers. 
                Soon you will be able to explore every corner of this realm.
              </p>
            </div>

            {/* Decorative Border Corners */}
            <div className="absolute top-2 left-2 w-12 h-12 border-t-2 border-l-2 rounded-tl-lg" style={{ borderColor: `${realm.theme.primary}50` }} />
            <div className="absolute top-2 right-2 w-12 h-12 border-t-2 border-r-2 rounded-tr-lg" style={{ borderColor: `${realm.theme.primary}50` }} />
            <div className="absolute bottom-2 left-2 w-12 h-12 border-b-2 border-l-2 rounded-bl-lg" style={{ borderColor: `${realm.theme.primary}50` }} />
            <div className="absolute bottom-2 right-2 w-12 h-12 border-b-2 border-r-2 rounded-br-lg" style={{ borderColor: `${realm.theme.primary}50` }} />
          </div>

          {/* Locations Preview */}
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {locations.map((location) => {
              const Icon = getLocationIcon(location.type);
              return (
                <button
                  key={location.id}
                  onClick={() => !location.locked && setSelectedLocation(location)}
                  disabled={location.locked}
                  className={`relative p-4 rounded-xl border text-left transition-all ${
                    location.locked
                      ? 'bg-black/30 border-gray-800/50 cursor-not-allowed opacity-60'
                      : 'bg-black/50 border-amber-900/30 hover:border-amber-500/50 hover:bg-black/60'
                  }`}
                  data-testid={`location-${location.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div 
                      className="p-2 rounded-lg"
                      style={{ 
                        backgroundColor: location.locked ? 'rgba(100,100,100,0.2)' : `${realm.theme.primary}20`
                      }}
                    >
                      {location.locked ? (
                        <Lock className="w-5 h-5 text-gray-500" />
                      ) : (
                        <Icon className="w-5 h-5" style={{ color: realm.theme.primary }} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-semibold truncate ${location.locked ? 'text-gray-500' : 'text-white'}`}>
                        {location.name}
                      </h3>
                      <p className="text-xs text-gray-500 capitalize">
                        {location.type.replace('_', ' ')}
                      </p>
                    </div>
                    {!location.locked && (
                      <ChevronRight className="w-5 h-5 text-amber-500/50" />
                    )}
                  </div>
                  {location.locked && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-[2px] rounded-xl">
                      <span className="text-xs text-gray-300 bg-black/80 px-3 py-1.5 rounded-lg font-medium border border-gray-700/50">Coming Soon</span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Location Detail Modal (for future use) */}
      {selectedLocation && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fadeIn">
          <div 
            className={`w-full max-w-md rounded-2xl overflow-hidden bg-gradient-to-br ${realm.theme.bgGradient} border border-amber-900/30`}
            data-testid="location-modal"
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <span className="text-xs uppercase tracking-wider" style={{ color: realm.theme.primary }}>
                    {selectedLocation.type.replace('_', ' ')}
                  </span>
                  <h2 className="font-heading text-2xl text-white">{selectedLocation.name}</h2>
                </div>
                <button
                  onClick={() => setSelectedLocation(null)}
                  className="p-2 rounded-full hover:bg-white/10 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5 text-white/70" />
                </button>
              </div>
              <p className="text-white/70 text-sm mb-6">
                This location is being prepared for exploration. 
                Soon you'll be able to interact with other travelers here.
              </p>
              <div className="flex items-center gap-2 text-white/50 text-sm">
                <Users className="w-4 h-4" />
                <span>0 travelers here</span>
              </div>
            </div>
            <div className="p-4 border-t border-white/10 bg-black/30">
              <button
                onClick={() => setSelectedLocation(null)}
                className="w-full py-3 rounded-xl font-semibold transition-all"
                style={{ 
                  backgroundColor: realm.theme.primary,
                  color: realm.id === 'niflheim' || realm.id === 'alfheim' ? '#000' : '#fff'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
